import { Directive, Input, OnInit, OnDestroy, inject, signal } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Subscription, combineLatest, of, timer } from 'rxjs';
import { map, startWith, distinctUntilChanged, switchMap, catchError } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { withoutViewport } from '../../../models/search-request';
import { selectCarState } from '../../../store/selectors/car.selectors';
import { selectRealEstateState } from '../../../store/selectors/real-estate.selectors';
import { selectAuthUser } from '../../../store/selectors/auth.selectors';
import { rectangle } from '../../../saved-searches/saved-search.model';
interface Response { total: number; facets: Record<string, { options: {value: boolean; count: number}[] }>; }
@Directive({selector: 'form[searchCountSummary]', standalone: true, exportAs: 'searchCounts'})
export class SearchCountSummaryDirective implements OnInit, OnDestroy {
  @Input({required:true}) searchCountSummary!: 'CAR' | 'REAL_ESTATE';
  @Input({required:true}) countForm!: FormGroup;
  private readonly features = new BehaviorSubject(false);
  @Input() set countFeatures(value: boolean) { this.features.next(value); }
  private readonly store = inject(Store);
  private readonly http = inject(HttpClient);
  readonly response = signal<Response | null>(null);
  private subscription?: Subscription;
  count(key: string, value?: boolean): number | undefined {
    const selected = this.countForm.get(key)?.value;
    if (typeof selected !== 'boolean' || (value !== undefined && selected !== value)) return undefined;
    return this.response()?.facets[key]?.options.find(option => option.value === selected)?.count;
  }
  total(): number | undefined { return this.response()?.total; }
  rangeTotal(prefix: string): number | undefined {
    return this.hasRange(prefix) ? this.total() : undefined;
  }
  private hasRange(prefix: string): boolean {
    return ['From', 'To'].some(suffix => {
      const control = this.countForm.get(prefix + suffix);
      const value = control?.value;
      return !!control?.valid && value !== null && value !== undefined && value !== '' &&
        /^-?\d+$/.test(String(value)) && Number.isSafeInteger(Number(value));
    });
  }
  ngOnInit(): void {
    const mode$ = this.searchCountSummary === 'CAR'
      ? this.store.select(selectCarState).pipe(map(s => ({isMap:s.isCarMapView, polygon:s.activePolygon, query:s.mapQuery})))
      : this.store.select(selectRealEstateState).pipe(map(s => ({isMap:s.isRealEstateMapView, polygon:s.activePolygon, query:s.mapQuery})));
    this.subscription = combineLatest([this.countForm.valueChanges.pipe(startWith(null)),
      this.countForm.statusChanges.pipe(startWith(null)), mode$, this.features, this.store.select(selectAuthUser)])
      .pipe(map(([, , mode, features, user]) => {
        const criteria: Record<string, unknown> = {...withoutViewport(this.countForm.getRawValue()),
          ...(mode.isMap ? mode.polygon?.length ? {polygonFilter: mode.polygon} : rectangle(mode.query) : {})};
        for (const key of ['limit','offset','sort','direction']) delete criteria[key];
        let valid = this.countForm.valid;
        // Reject invalid/incomplete numeric drafts without coercing decimals or changing form values.
        for (const key of Object.keys(criteria).filter(k => /From$|To$/.test(k))) {
          const v = criteria[key];
          if (v === '' || v === null || v === undefined) { delete criteria[key]; continue; }
          const n = typeof v === 'number' ? v : /^-?\d+$/.test(String(v)) ? Number(v) : NaN;
          if (!Number.isSafeInteger(n)) valid = false;
          else criteria[key] = n;
        }
        for (const key of Object.keys(criteria).filter(k => k.endsWith('From'))) {
          const to = criteria[key.slice(0,-4)+'To'];
          if (typeof to === 'number' && (criteria[key] as number) > to) valid = false;
        }
        const facets = [...['isForRent', 'isInstallmentAvailable'].filter(key => typeof criteria[key] === 'boolean'), ...(features ? Object.keys(criteria).filter(k =>
          typeof criteria[k] === 'boolean' && (k.startsWith('has') || ['isFurnished','isAccident','isManulaGear','withWarranty'].includes(k))) : [])];
        const needed = facets.length > 0 || Object.keys(criteria).some(key => key.endsWith('From') || key.endsWith('To'));
        return {criteria, facets, valid, needed, scope:[mode.isMap,user?.id,user?.role]};
      }), distinctUntilChanged((a,b) => JSON.stringify(a) === JSON.stringify(b)),
      switchMap(({criteria,facets,valid,needed}) => {
        this.response.set(null);
        if (!valid || !needed) return of(null);
        // Cancel both the debounce and HTTP request immediately on the next criteria change.
        return timer(500).pipe(switchMap(() => this.http.post<Response>(environment.apiUrl + '/api/' +
          (this.searchCountSummary === 'CAR' ? 'Cars' : 'RealEstate') + '/search/facets', {criteria,facets})),
          catchError(() => of(null)));
      })).subscribe(response => this.response.set(response));
  }
  ngOnDestroy(): void { this.subscription?.unsubscribe(); this.features.complete(); }
}
